import React, { useContext, useMemo } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import { applyFilters, Filters } from '../../core';

const versions = ['default', 'v2'] as const;

type Version = (typeof versions)[number];

type ContextType = {
  version: Version;
  is(version: Version): boolean;
  switchTo(version: Version): void;
};

const UiContext = React.createContext<ContextType | null>(null);

export const AutoSwitch = (variants: Record<Version, React.ComponentType>) => {
  const { version } = useUiVersion();
  const Component = variants[version];

  if (!Component) {
    throw new Error(`Missing component for UI version: ${version}`);
  }

  return <Component />;
};

export const useUiVersion = () => {
  const context = useContext(UiContext);

  if (!context) {
    throw new Error('UI Version provider not found!');
  }

  return context;
};

const UiVersionProvider = ({ children }: React.PropsWithChildren) => {
  const [version, setVersion] = useLocalStorageState<Version>(
    'quodoc_app_ui_version_v2',
    {
      defaultValue: applyFilters(Filters.app.uiVersion, 'v2'),
    },
  );

  const contextValue = useMemo(
    () => ({
      version,
      is(v: Version) {
        return v === version;
      },
      switchTo: setVersion,
    }),
    [version, setVersion],
  );

  return (
    <UiContext.Provider value={contextValue}>{children}</UiContext.Provider>
  );
};

export default UiVersionProvider;
